// Admin: set a colleague's mailbox up, and nothing more.
//
// Eleven people cannot each be walked through IMAP host names and app
// passwords, so IT does it once from here. This screen can WRITE a mailbox and
// TEST that it connects. It cannot list, read, reply to or export anybody's
// mail — the API has no route that would let it (see the header of
// src/api-mailbox/index.js). Setting a mailbox up for someone is an IT task;
// reading their mail is not.
//
// A configured mailbox does two things: it fills that person's Mail Inbox, and
// it becomes the account their quotations and invoices go out through, so the
// customer replies to the rep rather than to a shared address.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Mail, ShieldAlert } from 'lucide-react';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Spinner } from '@/shared/components/ui/Spinner';

interface MailboxSummary {
  host: string;
  port: number;
  secure: boolean;
  username: string;
  folder: string;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  fromName: string;
  isActive: boolean;
  hasPassword: boolean;
  lastPolledAt: string | null;
  lastError: string;
}

interface AdminMailboxRow {
  userId: string;
  username: string;
  displayName: string;
  role: string;
  department: string;
  active: boolean;
  mailbox: MailboxSummary | null;
}

/** The two providers RBM actually uses, so nobody types a host name by hand. */
const PRESETS = {
  'zoho-in': { label: 'Zoho (India — .in)', host: 'imap.zoho.in', port: 993, secure: true, smtpHost: 'smtp.zoho.in', smtpPort: 465, smtpSecure: true },
  'zoho-com': { label: 'Zoho (global — .com)', host: 'imap.zoho.com', port: 993, secure: true, smtpHost: 'smtp.zoho.com', smtpPort: 465, smtpSecure: true },
  gmail: { label: 'Gmail / Google Workspace', host: 'imap.gmail.com', port: 993, secure: true, smtpHost: 'smtp.gmail.com', smtpPort: 465, smtpSecure: true },
} as const;

type PresetKey = keyof typeof PRESETS;

/** Widened off the `as const` presets, so switching provider is assignable. */
interface MailboxForm {
  host: string;
  port: number;
  secure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  username: string;
  password: string;
  folder: string;
  fromName: string;
  isActive: boolean;
}

const fromPreset = (key: PresetKey): Omit<MailboxForm, 'username' | 'password' | 'folder' | 'fromName' | 'isActive'> => {
  const { label: _label, ...rest } = PRESETS[key];
  return rest;
};

const emptyForm = (): MailboxForm => ({
  ...fromPreset('zoho-in'),
  username: '',
  password: '',
  folder: 'INBOX',
  fromName: '',
  isActive: true,
});

function useAdminMailboxes() {
  return useQuery({
    queryKey: ['mailbox', 'admin', 'configs'],
    queryFn: async () => {
      const { data } = await api.get<{ users: AdminMailboxRow[]; encryptionAvailable: boolean }>(
        '/api/mailbox/admin/configs',
      );
      return data;
    },
  });
}

export function TeamMailboxesTab() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useAdminMailboxes();
  const [editing, setEditing] = useState<AdminMailboxRow | null>(null);
  const [form, setForm] = useState<MailboxForm>(emptyForm());

  const save = useMutation({
    mutationFn: async ({ userId, body }: { userId: string; body: Record<string, unknown> }) =>
      (await api.put(`/api/mailbox/admin/config/${userId}`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mailbox', 'admin', 'configs'] });
      toast.success('Mailbox saved.');
      setEditing(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save the mailbox.')),
  });

  const test = useMutation({
    mutationFn: async (userId: string) =>
      (await api.post<{ ok: boolean; message?: string; error?: string }>(`/api/mailbox/admin/test/${userId}`, {})).data,
    onSuccess: (res) => (res.ok ? toast.success(res.message || 'Connected.') : toast.error(res.error || 'Could not connect.')),
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not test the mailbox.')),
  });

  const openFor = (row: AdminMailboxRow) => {
    const m = row.mailbox;
    setForm({
      ...emptyForm(),
      ...(m
        ? {
            host: m.host, port: m.port, secure: m.secure,
            smtpHost: m.smtpHost, smtpPort: m.smtpPort, smtpSecure: m.smtpSecure,
            username: m.username, folder: m.folder || 'INBOX',
            fromName: m.fromName || row.displayName, isActive: m.isActive,
          }
        : { fromName: row.displayName }),
      // Never prefilled — the server does not return it, in any form. Left
      // blank on save, the stored one is kept.
      password: '',
    });
    setEditing(row);
  };

  const applyPreset = (key: PresetKey) => setForm((f) => ({ ...f, ...fromPreset(key) }));

  const submit = () => {
    if (!editing) return;
    if (!form.username.trim()) { toast.error('The email address is required.'); return; }
    if (!editing.mailbox?.hasPassword && !form.password) {
      toast.error('An app password is required the first time a mailbox is set up.');
      return;
    }
    save.mutate({
      userId: editing.userId,
      body: {
        host: form.host, port: form.port, secure: form.secure,
        smtpHost: form.smtpHost, smtpPort: form.smtpPort, smtpSecure: form.smtpSecure,
        username: form.username.trim(), folder: form.folder.trim() || 'INBOX',
        fromName: form.fromName.trim(), isActive: form.isActive,
        ...(form.password ? { password: form.password } : {}),
      },
    });
  };

  if (isLoading) return <div className="flex justify-center p-8"><Spinner /></div>;
  if (isError || !data) return <p className="p-6 text-destructive">Could not load the team's mailboxes.</p>;

  return (
    <>
      {!data.encryptionAvailable && (
        <Card className="mb-4 border-destructive/40">
          <CardBody className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
            <div>
              <p className="font-semibold text-on-surface">Mailbox passwords cannot be stored yet</p>
              <p className="text-data-md text-on-surface-variant">
                This server has no secret-encryption key, so a mailbox password would have to be
                written in the clear — which it refuses to do. Set <code>INTEGRATION_ENCRYPTION_KEY</code>{' '}
                in the server environment and restart. Set it once and do not change it later: every
                stored password is encrypted with a key derived from it.
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Team Mailboxes"
          subtitle="Each person's own IMAP inbox and the account their quotations go out from. Passwords are stored encrypted and are never shown again."
        />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-data-md">
              <thead className="bg-muted/50 text-left text-xs uppercase">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Department</th>
                  <th className="px-4 py-3">Mail address</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.users.map((row) => (
                  <tr key={row.userId} className="border-t">
                    <td className="px-4 py-3">
                      <span className="font-medium text-on-surface">{row.displayName}</span>
                      <span className="block text-xs text-on-surface-variant">{row.username} · {row.role}</span>
                    </td>
                    <td className="px-4 py-3 capitalize">{row.department || '—'}</td>
                    <td className="px-4 py-3">{row.mailbox?.username || <span className="text-on-surface-variant">—</span>}</td>
                    <td className="px-4 py-3">
                      {!row.mailbox ? (
                        <Badge tone="neutral">Not set up</Badge>
                      ) : row.mailbox.lastError ? (
                        <Badge tone="danger" title={row.mailbox.lastError}>Failing</Badge>
                      ) : row.mailbox.isActive ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="warning">Paused</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openFor(row)}>
                        {row.mailbox ? 'Edit' : 'Set up'}
                      </Button>
                      {row.mailbox && (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={test.isPending}
                          onClick={() => test.mutate(row.userId)}
                        >
                          Test
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing ? `Mailbox for ${editing.displayName}` : ''}
        size="lg"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
              <Button key={key} size="sm" variant="secondary" onClick={() => applyPreset(key)}>
                {PRESETS[key].label}
              </Button>
            ))}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">Email address (also the login)</span>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="sales1@rbmoilcorporation.in" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">
                App password {editing?.mailbox?.hasPassword && <span className="text-on-surface-variant">(leave blank to keep the stored one)</span>}
              </span>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="new-password" />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">IMAP host</span>
              <Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">IMAP port</span>
              <Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) || 0 })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">SMTP host</span>
              <Input value={form.smtpHost} onChange={(e) => setForm({ ...form, smtpHost: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">SMTP port</span>
              <Input type="number" value={form.smtpPort} onChange={(e) => setForm({ ...form, smtpPort: Number(e.target.value) || 0 })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">Display name on outgoing mail</span>
              <Input value={form.fromName} onChange={(e) => setForm({ ...form, fromName: e.target.value })} />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-data-md font-medium">Folder</span>
              <Input value={form.folder} onChange={(e) => setForm({ ...form, folder: e.target.value })} />
            </label>
          </div>

          <label className="flex items-center gap-2 text-data-md">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
            Poll this mailbox every 5 minutes
          </label>

          {/* The one thing that catches everybody out, said once, where it
              matters. Neither provider accepts a webmail password over
              IMAP/SMTP once two-factor is on — Google not at all. */}
          <p className="flex items-start gap-2 rounded bg-muted/50 p-3 text-data-md text-on-surface-variant">
            <Mail className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            Use an <strong className="font-semibold">app-specific password</strong>, not the webmail
            login password — Zoho requires one when two-factor is on, and Google requires one always.
          </p>

          <div className="flex justify-end gap-3 border-t border-outline pt-4">
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={save.isPending}>Cancel</Button>
            <Button onClick={submit} loading={save.isPending}>Save mailbox</Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default TeamMailboxesTab;
